import { useAccountInfo } from 'hooks/billing/useAccountInfo';
import { useQuota } from 'hooks/billing/useQuota';
import { isAccountInTrial } from 'utils/billing';
import { isPaidPlan } from 'utils/billing/isTrialUiActive';

/**
 * General-purpose billing state hook — aggregates /account-info and /quota into
 * derived billing flags shared across the app.
 *
 * @returns {isAccountInTrial} Whether the account is currently in an active trial.
 * @returns {isTrialLapsed} FE safety net: trial end date has passed and the account
 *   has not converted to a paid plan. Guards the window where the trial expires
 *   before/without the BE setting `quota.blocked: trial-end` (PLT-4196).
 *   Returns false while quota is loading (unknown plan state must not flash the
 *   trial-ended surface at a paid account).
 * @returns {isLoading} Whether account-info is still loading.
 * @returns {isError} Whether the account-info fetch failed.
 */
export const useAccountBilling = () => {
  const { data: accountInfo, isLoading, isError } = useAccountInfo();
  const {
    data: quota,
    isLoading: isQuotaLoading,
    isError: isQuotaError,
  } = useQuota();

  const trial = accountInfo?.trial ?? null;
  const quotaResolved = !isQuotaLoading && !isQuotaError && quota !== undefined;

  return {
    isAccountInTrial: isAccountInTrial(accountInfo),
    isTrialLapsed:
      quotaResolved &&
      !!(trial && !trial.active && !isPaidPlan(quota.plan ?? null)),
    isLoading,
    isError,
  };
};
